import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import Globals from '../../../../store/global-selectors';
import { useForm } from '../../../hooks/use-form';
import { FORM_REQUIREMENTS, REGISTRATION_REQUIREMENTS, SUPPORTED_PAYMENT_METHOD_TYPES } from '../../../utilities/constants';
import { BUSINESS } from '../../constants';
import { useSubmitRegistration } from '../hooks/use-submit-registration';

const WalletRegistrationFormContext = React.createContext({
  initialFormValues: undefined,
  setPerformFormValidation: noop,
  walletForm: undefined,
  setIsPaymentComplete: noop,
  setAdyenSubmitError: noop,
  hasSubmittedPayment: false,
  setHasSubmittedPayment: noop,
  /** @type {(path: string[], value: unknown) => void} */
  updateWalletForm: noop,
  submittingRegistration: false,
});
export const useWalletRegistrationFormContext = () => useContext(WalletRegistrationFormContext);

const findPreferredCreditCardPaymentType = (paymentMethodTypes) => {
  if (paymentMethodTypes.includes(SUPPORTED_PAYMENT_METHOD_TYPES.ADYEN_DROP_IN_COMPONENT)) {
    return SUPPORTED_PAYMENT_METHOD_TYPES.ADYEN_DROP_IN_COMPONENT;
  }
  if (paymentMethodTypes.includes(SUPPORTED_PAYMENT_METHOD_TYPES.CREDIT_CARD)) {
    return SUPPORTED_PAYMENT_METHOD_TYPES.CREDIT_CARD;
  }
};

const getDefaultLabelProviders = (newLabelProviderIds = [], nonDefaultTerms = {}) => {
  return newLabelProviderIds.filter((labelProviderId) => !Object.keys(nonDefaultTerms).includes(labelProviderId));
};

const buildInitialWalletForm = (countryCode, walletToRegister, registrationRequirements, consolidatedTerms, nonDefaultTerms, email) => {
  const initialForm = {
    [FORM_REQUIREMENTS.WALLET_ID]: walletToRegister.walletId,
    [FORM_REQUIREMENTS.CURRENCY_CODE]: walletToRegister.currencyCode,
    [FORM_REQUIREMENTS.BILLING_ADDRESS]: {
      countryCode,
      email,
    },
    [FORM_REQUIREMENTS.PICKUP_ADDRESS]: {
      countryCode,
    },
    [FORM_REQUIREMENTS.METADATA]: {
      [FORM_REQUIREMENTS.TERMS]: false,
      [FORM_REQUIREMENTS.ACCEPTED_TERMS]: consolidatedTerms,
      [FORM_REQUIREMENTS.IO_BLACK_BOX]: null,
      [FORM_REQUIREMENTS.NONDEFAULT_TERMS]: false,
      [FORM_REQUIREMENTS.UPS_TERMS]: false,
    },
    [FORM_REQUIREMENTS.RESOURCES]: {
      [FORM_REQUIREMENTS.LABEL_PROVIDER_IDS]: getDefaultLabelProviders(walletToRegister?.newLabelProviderIds, nonDefaultTerms),
      [FORM_REQUIREMENTS.INSURANCE_PROVIDER_IDS]: walletToRegister.newInsuranceProviderIds,
      [FORM_REQUIREMENTS.RESOURCE_UPDATE_IDS]: walletToRegister.newResourceUpdates?.map((update) => update.updateId),
    },
    [FORM_REQUIREMENTS.ACCOUNT_TYPE]: BUSINESS,
  };

  if (registrationRequirements[REGISTRATION_REQUIREMENTS.PAYMENT_METHOD]) {
    const { satisfyingFormTypes } = registrationRequirements[REGISTRATION_REQUIREMENTS.PAYMENT_METHOD];

    const creditCardFormType = findPreferredCreditCardPaymentType(satisfyingFormTypes);

    if (creditCardFormType) {
      initialForm[FORM_REQUIREMENTS.PAYMENT_METHOD] = {
        [FORM_REQUIREMENTS.PAYMENT_METHOD_TYPE]: creditCardFormType,
      };
    }
  }

  return initialForm;
};

/**
 * @typedef {object} WalletToRegister
 * @property {string} walletId
 * @property {string} currencyCode
 * @property {string[]} [newLabelProviderIds]
 * @property {string[]} [newInsuranceProviderIds]
 * @property {Array<{updateId: string}>} [newResourceUpdates]
 */

/**
 * @typedef {object} RegistrationRequirement
 * @property {string[] | null} satisfyingFormTypes
 */

/**
 * @typedef {Record<string, RegistrationRequirement>} RegistrationRequirements
 */

/**
 * @typedef {object} Term
 * @property {string} termType
 * @property {string} version
 */

/**
 * @typedef {Record<string, Term[]>} NonDefaultTerms
 */

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {WalletToRegister} props.walletToRegister
 * @param {RegistrationRequirements} props.registrationRequirements
 * @param {Term[]} props.consolidatedTerms
 * @param {NonDefaultTerms} [props.nonDefaultTerms]
 * @param {Array<{fulfillmentPlanId: string, shipFromId: string}>} [props.postWalletRegistrationSuccessPayload]
 * @param {object} [props.initialFormValues]
 * @returns {JSX.Element}
 */
export const WalletRegistrationFormContextProvider = ({
  children,
  walletToRegister,
  registrationRequirements,
  consolidatedTerms,
  nonDefaultTerms,
  postWalletRegistrationSuccessPayload = emptyArray,
  initialFormValues,
}) => {
  const countryCode = useSelector(Globals.selectSellerHomeCountry);
  const [isVerifiedAddress, setIsVerifiedAddress] = useState(false);
  const [hasSubmittedPayment, setHasSubmittedPayment] = useState(false);
  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [adyenSubmitError, setAdyenSubmitError] = useState(false);
  const [performValidateForm, setPerformFormValidation] = useState();
  const handleSetPerformFormValidation = useCallback(
    (value) => {
      setPerformFormValidation(() => value);
    },
    [setPerformFormValidation],
  );

  const verifiedSellerEmail = useSelector(Globals.selectSellerEmail);

  const { form: walletForm, updateForm: updateWalletForm } = useForm(
    initialFormValues ??
      buildInitialWalletForm(
        countryCode,
        walletToRegister,
        registrationRequirements,
        consolidatedTerms,
        nonDefaultTerms,
        verifiedSellerEmail,
      ),
  );

  const billingAddress = useMemo(() => {
    return walletForm?.[FORM_REQUIREMENTS.BILLING_ADDRESS];
  }, [walletForm]);

  const { loading: submittingRegistration, submitRegistration } = useSubmitRegistration();

  const value = useMemo(
    () => ({
      updateWalletForm,
      walletForm,
      submitRegistration,
      submittingRegistration,
      postWalletRegistrationSuccessPayload,
      billingAddress,
      isVerifiedAddress,
      setIsVerifiedAddress,
      hasSubmittedPayment,
      setHasSubmittedPayment,
      isPaymentComplete,
      setIsPaymentComplete,
      adyenSubmitError,
      setAdyenSubmitError,
      performValidateForm,
      setPerformFormValidation: handleSetPerformFormValidation,
    }),
    [
      updateWalletForm,
      walletForm,
      submitRegistration,
      submittingRegistration,
      postWalletRegistrationSuccessPayload,
      billingAddress,
      isVerifiedAddress,
      hasSubmittedPayment,
      isPaymentComplete,
      adyenSubmitError,
      performValidateForm,
      handleSetPerformFormValidation,
    ],
  );

  return <WalletRegistrationFormContext.Provider value={value}>{children}</WalletRegistrationFormContext.Provider>;
};
